import { Complaint } from "@features/cx/complaints/types";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  CheckSquare,
  FileEdit,
  FileText,
  ImageIcon,
  Info,
  X,
} from "lucide-react";
import { toast } from "@shared/ui/use-toast";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { useServiceOrders } from "@features/cx/shared/hooks/useServiceOrder";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { FilePreview } from "@shared/components/FilePreview";
interface ClosureRequirements {
  customerConfirmation: boolean;
  qualityAssuranceDone: boolean;
}

interface ComplaintDetailsTabProps {
  request: Complaint;
  onResolve?: (closureRequirements: any) => void;
  onReject?: (requestId: string) => void;
  onRejectConfirm?: (reason: string) => void;
  closureRequirements?: any;
  setClosureRequirements?: any;
}

const ComplaintDetailsTab: React.FC<ComplaintDetailsTabProps> = ({
  request,
  onResolve,
  onReject,
  onRejectConfirm,
  closureRequirements,
  setClosureRequirements,
}) => {

  // Placeholder: These would come from additionalData or related API fields
  const additionalData = (request as any)?.additionalData || {};
  const evidence = (request as any)?.evidence || [];
  const attachments = (request as any)?.file || [];
  const incidentDate = (request as any)?.incidentDate || "";
  const dueDate = request?.slaSummary.slaDueDate;
  const isPastDue = dueDate && new Date(dueDate) < new Date();
   const { serviceOrders } = useServiceOrders(
          {
            remote_utility_id: Number(getRemoteUtilityId()),
            request_type: "service-order",
            request_id: request?.id,
          },
          { enabled: !!request?.id },
        );
        
  const hasApprovedOrInProgressSOP =
      serviceOrders?.some(
        (serviceOrder) =>
          [
            "Created",
            "Not Assigned",
            "In-Progress",
            "Assigned",
            "Overdue",
            "Completed",
            "Accepted",
            "Refused",
            "Incomplete",
          ].includes(serviceOrder?.statusDisplay),
      ) || false;
      console.log("hasApprovedOrInProgressSOP", hasApprovedOrInProgressSOP,serviceOrders);
  const daysDue = dueDate
    ? Math.max(
      0,
      Math.ceil(
        (new Date(dueDate).getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24)
      )
    )
    : null;

  const currentStatus = request?.statusDisplay?.toLowerCase();

  const complaintDetails = [
    {
      label: "COMPLAINT ID",
      value: request?.requestNo,
    },
    { label: "NAME", value: request?.utilitySupportRequest?.name },
    {
      label: "CODE",
      value: request?.utilitySupportRequest?.configurationCode,
    },
    {
      label: "CATEGORY",
      value: request?.categoryDisplay,
    },
    {
      label: "SUB-CATEGORY",
      value: request?.subCategoryDisplay,
    },
    {
      label: "PRIORITY",
      value: request?.priorityLevelDisplay ?? "NA",
    },
    { label: "UTILITY SERVICE", value: request?.utilitySupportRequest?.productCode?.join(", ") },
    {
      label: "INCIDENT DATE",
      value:
        request?.requestDate,
    },
    { label: "DUE DATE", value: dueDate || "-" },
  ];

  const slaTarget = [
    {
      label: "Acknowledgement",
      value:
        request?.utilitySupportRequest?.extraData
          ?.acknowlegmentTime ?? "NA",
    },
    {
      label: "Response",
      value:
        request?.utilitySupportRequest?.extraData
          ?.responseTime ?? "NA",
    },
    {
      label: "Resolution",
      value:
        request?.utilitySupportRequest?.extraData
          ?.resolutionTime ?? "NA",
    },
  ];

  const slaAchieved = [

    {
      label: "Acknowledgement",
      value: request?.acknowledgementTimeAchieved ?? "Pending",
      color: "text-green-600",
    },
    {
      label: "Response",
      value: request?.responseTime ?? "Pending",
      color: "text-green-600",
    },
    {
      label: "Resolution",
      value: request?.resolutionTimeAchieved ?? "Pending",
      color: "text-green-600",
    },
  ];

  const handleClosureRequirementChange = (
    requirement: keyof ClosureRequirements,
    checked: boolean
  ) => {
    setClosureRequirements((prev) => ({
      ...prev,
      [requirement]: checked,
    }));
  };

  const handleResolveRequest = () => {
    if (request?.utilitySupportRequest?.extraData?.requiresApproval) {
      if (
        closureRequirements.customerConfirmation &&
        closureRequirements.qualityAssuranceDone
      ) {
        if (onResolve) {
          onResolve(closureRequirements);
        }
      } else {
        toast({
          title: "Error",
          description:
            "Please complete all closure requirements before resolving the complaint.",
          variant: "destructive",
        });
      }
    } else {
      if (onResolve) {
        onResolve(closureRequirements);
      }
    }
  };

  const handleRejectRequest = () => {
    if (onReject) {
      onReject(request?.id);
    }
  };

  return (
    <Card className="p-0 border-none shadow-none">
      <CardContent>
        <div className="space-y-6">
          {/* Complaint Details Section */}
          <div className="space-y-3">
            <h4 className="card-heading-text flex items-center gap-2">
              <FileText className="h-4 w-4" /> Complaint Details
            </h4>
            <div className="space-y-4 border border-gray-200 p-4 rounded-md">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {complaintDetails.map((field, idx) => (
                  <ValueLabel
                    key={idx}
                    label={field.label}
                    value={field.value}
                  />
                ))}
              </div>


              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-2 rounded mt-4">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 text-yellow-400" />
                  <span className="ml-2 text-xs text-yellow-800">
                    {dueDate ? (
                      isPastDue ? (
                        <>This complaint is due in 0 days. It is past due!</>
                      ) : (
                        <>This complaint is due in {daysDue} days.</>
                      )
                    ) : (
                      <>Due date not available.</>
                    )}
                  </span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md">
              {/* SLA Target */}
              <div className="border border-gray-200 p-4 rounded-md">
                <p className="card-heading-text mb-4">SLA Target</p>
                <div className="grid grid-cols-3 gap-2">
                  {slaTarget.map((field, idx) => (
                    <ValueLabel
                      key={idx}
                      label={field.label}
                      value={field.value}
                      className="text-center [&>div]:justify-center"
                    />
                  ))}
                </div>
              </div>

              {/* SLA Achieved */}
              <div className="border border-gray-200 p-4 rounded-md">
                <p className="card-heading-text mb-4">SLA Achieved</p>
                <div className="grid grid-cols-3 gap-2">
                  {slaAchieved.map((field, idx) => (
                    <ValueLabel
                      key={idx}
                      label={field.label}
                      value={<span className={field.color}>{field.value}</span>}
                      className="text-center [&>div]:justify-center"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Description & Resolution */}
            <div>
              <h4 className="card-heading-text flex items-center gap-2">
                <CheckCircle className="h-4 w-4" /> Expected resolution
              </h4>
              <div className="space-y-2 border border-gray-200 p-4 rounded-md mt-2">
                <ValueLabel label="Customer's Expected Resolution" value={additionalData.expectedResolution || "No description provided."} />
              </div>
            </div>
            <div>
              <h4 className="card-heading-text flex items-center gap-2">
                <FileEdit className="h-4 w-4 text-blue-600" /> Additional Notes
              </h4>
              <div className="space-y-2 border border-gray-200 p-4 rounded-md mt-2">
                <ValueLabel label="Notes" value={additionalData.additionalNotes || "No description provided."} />
              </div>
            </div>

            {/* Evidence & Images - Full Row */}
            <div>
              <h4 className="card-heading-text flex items-center gap-2">
                <ImageIcon className="h-4 w-4" /> Evidence & Images
              </h4>
              <div className="space-y-4 border border-gray-200 p-4 rounded-md mt-2">

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                  {request?.file ? (
                    <FilePreview
                      url={request.file}
                      createdDate={request.createdDate}
                    />
                  ) : (
                    <div className="col-span-full py-8 text-center border-2 border-dashed rounded-lg bg-gray-50/50">
                      <ImageIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No evidence images provided.</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-2 rounded mt-4 flex items-center">
                <Info className="h-4 w-4 text-blue-400" />
                <span className="ml-2 text-xs text-blue-800">
                  All attachments are securely stored and can only be accessed by
                  authorized personnel.
                </span>
              </div>
            </div>

            {/* Closure Requirements */}
            <div className="space-y-3">
              <h4 className="card-heading-text flex items-center gap-2 mb-0">
                <CheckSquare className="h-4 w-4" />
                Closure Requirements
              </h4>
              <div className="space-y-4 border border-gray-200 p-4 rounded-md">
                <p className="text-xs text-neutral-500 mt-0">
                  Please complete the following requirements before marking this
                  complaint as resolved:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="customerConfirmation"
                      checked={
                        currentStatus === "completed" ||
                        currentStatus === "rejected"
                          ? additionalData?.closureRequirements
                              ?.customerConfirmation
                          : closureRequirements?.customerConfirmation
                      }
                      disabled={
                        currentStatus === "completed" ||
                        currentStatus === "rejected"
                      }
                      onCheckedChange={(checked) =>
                        handleClosureRequirementChange(
                          "customerConfirmation",
                          checked as boolean
                        )
                      }
                    />
                    <label
                      htmlFor="customerConfirmation"
                      className="text-sm cursor-pointer"
                    >
                      <div className="font-medium">Customer Confirmation</div>
                      <p className="card-subheading-text">
                        Confirm that the customer has acknowledged the resolution
                        and is satisfied with the outcome.
                      </p>
                    </label>
                  </div>

                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="qualityAssuranceDone"
                      checked={
                        currentStatus === "completed" ||
                        currentStatus === "rejected"
                          ? additionalData?.closureRequirements
                              ?.qualityAssuranceDone
                          : closureRequirements?.qualityAssuranceDone
                      }
                      disabled={
                        currentStatus === "completed" ||
                        currentStatus === "rejected"
                      }
                      onCheckedChange={(checked) =>
                        handleClosureRequirementChange(
                          "qualityAssuranceDone",
                          checked as boolean
                        )
                      }
                    />
                    <label
                      htmlFor="qualityAssuranceDone"
                      className="text-sm cursor-pointer"
                    >
                      <div className="font-medium">Quality Assurance Done</div>
                      <p className="card-subheading-text">
                        Verify that all quality checks have been completed and the
                        resolution meets company standards.
                      </p>
                    </label>
                  </div>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-2 rounded mt-4 flex items-center">
                  <AlertTriangle className="h-4 w-4 text-yellow-400" />
                  <span className="ml-2 text-xs text-yellow-800">
                    Both requirements must be completed before the complaint can be
                    marked as resolved.
                  </span>
                </div>

                {currentStatus !== "completed" && currentStatus !== "rejected" && (
                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                    <Button
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
                      onClick={handleRejectRequest}
                      disabled={hasApprovedOrInProgressSOP}
                    >
                      Reject Request
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700 text-white disabled:bg-green-200"
                      onClick={handleResolveRequest}
                      disabled={
                        !closureRequirements?.customerConfirmation ||
                        !closureRequirements?.qualityAssuranceDone || hasApprovedOrInProgressSOP
                      }
                    >
                      Mark as Resolved
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ComplaintDetailsTab;
